import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExerciseDefinition } from '../../types';
import { BaseApiService } from '../../api/services/BaseApiService';
import InstagramEmbed from '../common/InstagramEmbed';
import { apiServices } from '../../api/services';
import { ExerciseProgressPoint } from '../../api/services/ExerciseService';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/provider';
import ConfirmationModal from '../common/ConfirmationModal';
import styles from './ExerciseDetailModal.module.css';

/**
 * TODO.md I2: normalized shape the chart below actually renders. GET /exercises/:id/progress returns
 * a per-exerciseType-branched shape (see ExerciseProgressPoint) - `value` here is `maxWeight` for a
 * strength row or `maxHoldTimeSeconds` for a stretching/mobility row, so the chart's scale/position/
 * tooltip math stays exerciseType-agnostic. `unit` is only ever set for strength rows.
 */
interface ProgressChartPoint {
  date: string;
  value: number;
  unit?: string;
  isAutofill?: boolean;
}

interface ExerciseDetailModalProps {
  exercise: ExerciseDefinition | null;
  onClose: () => void;
  onAddToWorkout: (exercise: ExerciseDefinition) => void;
  onToggleFavorite: (exercise: ExerciseDefinition) => void;
  onEditExercise: (exercise: ExerciseDefinition) => void;
  isFavorite: boolean;
  maxWeightData?: { maxWeight: number; unit: string; maxRepsForWeight: number; exerciseName: string; date: string; workoutId: string } | null;
  onBackToTemplate?: () => void;
}

const ExerciseDetailModal: React.FC<ExerciseDetailModalProps> = ({
  exercise,
  onClose,
  onAddToWorkout,
  onToggleFavorite,
  onEditExercise,
  isFavorite,
  maxWeightData,
  onBackToTemplate,
}) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isPublicMode = !isAuthenticated;
  // TODO.md I2: drives both the chart title/labels and which field of a fetched row is meaningful.
  const isHoldTimeType = exercise?.exerciseType === 'stretching' || exercise?.exerciseType === 'mobility';
  const [referencesExpanded, setReferencesExpanded] = useState(true);
  const [progressData, setProgressData] = useState<ProgressChartPoint[]>([]);
  const [hoveredPoint, setHoveredPoint] = useState<(ProgressChartPoint & { x: number; y: number }) | null>(null);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // TODO.md I2: GET /exercises/:id/progress branches per row on exerciseType (see
  // ExerciseProgressPoint) - normalize to a single `value` field so the chart below doesn't need to
  // know which backend field it came from.
  const normalizeProgressData = (data: ExerciseProgressPoint[]): ProgressChartPoint[] =>
    data.map(point =>
      'maxHoldTimeSeconds' in point
        ? { date: point.date, value: point.maxHoldTimeSeconds }
        : { date: point.date, value: point.maxWeight, unit: point.unit }
    );

  useEffect(() => {
    if (exercise && !isPublicMode) {
      apiServices.exercises.getExerciseProgress(exercise.id)
        .then(data => {
          // Autofill missing dates in 3-month period
          const autofilledData = autofillProgressData(normalizeProgressData(data));
          setProgressData(autofilledData);
        })
        .catch(error => {
          console.error('Failed to fetch exercise progress:', error);
          setProgressData([]);
        });
    }

    if (exercise && isPublicMode) {
      setProgressData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exercise, isPublicMode]);

  // Generate autofill data for 3-month period
  const autofillProgressData = (actualData: ProgressChartPoint[]): ProgressChartPoint[] => {
    if (actualData.length === 0) return actualData;

    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    const today = new Date();
    const normalizeDateKey = (d: string) => d.split('T')[0];
    const dataMap = new Map(actualData.map(d => [normalizeDateKey(d.date), d]));

    // Start with all actual data dates to ensure they're included
    const actualDates = Array.from(dataMap.keys()).sort();
    const allDates = new Set<string>();

    // Add all actual data dates
    actualDates.forEach(date => allDates.add(date));

    // Generate weekly dates from 3 months ago to fill gaps
    const currentDate = new Date(threeMonthsAgo);
    while (currentDate <= today) {
      const dateStr = currentDate.toISOString().split('T')[0];
      allDates.add(dateStr);
      // Move to next week
      currentDate.setDate(currentDate.getDate() + 7);
    }

    // Create final array with sorted dates
    const autofilled: ProgressChartPoint[] = [];

    Array.from(allDates)
      .sort()
      .forEach(dateStr => {
        const existingData = dataMap.get(dateStr);

        if (existingData) {
          autofilled.push({ ...existingData, date: dateStr, isAutofill: false });
        } else {
          // Add blank data point with a sentinel value and mark as autofill
          autofilled.push({
            date: dateStr,
            value: -1, // Use -1 to distinguish from a legitimate 0 (bodyweight/hold-time can be 0)
            unit: actualData[0]?.unit,
            isAutofill: true
          });
        }
      });

    return autofilled;
  };

  // Log when maxWeightData changes
  useEffect(() => {
    console.log('ExerciseDetailModal - maxWeightData:', maxWeightData);
  }, [maxWeightData]);

  const internalOnClose = () => {
    onClose();
  };

  const handleDelete = async () => {
    if (!exercise) return;

    try {
      setIsDeleting(true);
      await apiServices.exercises.deleteExercise(exercise.id);
      try {
        sessionStorage.setItem('exercises_force_reload', '1');
      } catch {
        // ignore storage errors
      }
      setShowDeleteConfirm(false);
      onClose();
      navigate('/exercises');
    } catch (e: any) {
      const status = e?.status || e?.response?.status;
      const message = e?.message || e?.response?.data?.message;

      if (status === 409) {
        alert(message || t('exercises.alerts.deleteBlockedByTemplate'));
      } else {
        alert(message || t('exercises.alerts.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // Check if user can edit this exercise
  const canEdit = user && (user.id === exercise?.userId || user.username === 'admin_simple');
  const canUseProtectedActions = !isPublicMode;

  const formatMuscleGroupLabel = (group: string) => {
    const fallback = group.charAt(0).toUpperCase() + group.slice(1);
    return t(`muscleGroups.${group}`, { defaultValue: fallback });
  };

  // Parse description to convert bullet points to list
  const parseDescription = (description: string) => {
    if (!description) return null;
    
    const lines = description.split('\n').filter(line => line.trim());
    const bulletLines = lines.filter(line => line.trim().startsWith('• '));
    
    if (bulletLines.length === 0) {
      return <p>{description}</p>;
    }
    
    const nonBulletLines = lines.filter(line => !line.trim().startsWith('• '));
    
    return (
      <>
        {nonBulletLines.map((line, index) => (
          <p key={`text-${index}`}>{line}</p>
        ))}
        <ul className={styles.descriptionList}>
          {bulletLines.map((line, index) => (
            <li key={`bullet-${index}`}>{line.trim().substring(2)}</li>
          ))}
        </ul>
      </>
    );
  };

  if (!exercise) return null;

  const displayName = exercise.localizedName || exercise.name;
  const displayDescription = exercise.localizedDescription || exercise.description;
  // Detail view only (TODO.md B4, AC-EX-20) - notes/localizedNotes are stripped from list/search
  // responses server-side, so this is only ever populated when `exercise` came from a single-exercise
  // fetch (GET /exercises/:id), same as how this modal is always opened. Same
  // localizedX-falls-back-to-x pattern as displayDescription above.
  const displayNotes = exercise.localizedNotes || exercise.notes;

  return (
    <div className="modal-overlay" onClick={internalOnClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button
          className={styles.closeButton}
          onClick={internalOnClose}
          aria-label={t('exercises.modal.close')}
        >
          ✕
        </button>
        <div className={styles.detailCard}>
          <div className={styles.detailHeader}>
            <img
              src={BaseApiService.resolveImageUrl(exercise.imageUrl)}
              alt={`${displayName} preview`}
              className={styles.detailImage}
              loading="lazy"
            />
            <div className={styles.detailInfo}>
              <div className={styles.detailName}>
                {displayName}
              </div>
              <div className={styles.detailMuscle}>
                {formatMuscleGroupLabel(exercise.muscleGroup)}
              </div>
              {(exercise.exerciseType || (exercise.categories && exercise.categories.length > 0)) && (
                <div className={styles.tagsRow} data-testid="exercise-detail-tags">
                  {exercise.exerciseType && (
                    <span
                      className={`${styles.tag} ${styles.typeTag}`}
                      data-testid="exercise-detail-type-tag"
                    >
                      {t(`exerciseTypes.${exercise.exerciseType}`, { defaultValue: exercise.exerciseType })}
                    </span>
                  )}
                  {(exercise.categories || []).map(category => (
                    <span
                      key={category}
                      className={styles.tag}
                      data-testid={`exercise-detail-category-tag-${category}`}
                    >
                      {t(`exerciseCategories.${category}`, { defaultValue: category })}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={styles.detailText}>
            {displayDescription ? parseDescription(displayDescription) : t('exercises.modal.noDescription')}
          </div>

          {displayNotes && (
            <div className={styles.detailText} data-testid="exercise-detail-notes">
              <div style={{ opacity: 0.7, fontSize: 13, marginBottom: 4 }}>
                {t('exercises.modal.notesTitle')}
              </div>
              <p>{displayNotes}</p>
            </div>
          )}

          {maxWeightData && maxWeightData.maxWeight > 0 && maxWeightData.maxRepsForWeight > 0 && (
            <div className={styles.personalRecord}>
              <div className={styles.prHeader}>
                <span className={styles.prIcon}>🏆</span>
                <h4 className={styles.prTitle}>{t('exercises.modal.personalRecord')}</h4>
              </div>
              <div className={styles.prStats}>
                <div className={styles.prStat}>
                  <span className={styles.prLabel}>{t('exercises.modal.maxWeight')}</span>
                  <span className={styles.prValue}>{maxWeightData.maxWeight || 0} {maxWeightData.unit}</span>
                </div>
                <div className={styles.prStat}>
                  <span className={styles.prLabel}>{t('exercises.modal.repsForMax')}</span>
                  <span className={styles.prValue}>{maxWeightData.maxRepsForWeight}</span>
                </div>
              </div>
            </div>
          )}
          {/* TODO.md I2: deliberately NOT gated behind maxWeightData (unlike the PR-cards block above).
              maxWeightData comes from the legacy weight-only /max-weights/:exerciseName endpoint,
              which is null whenever no set ever has weight > 0 - true for every stretching/mobility
              exercise (hold-time sets always store weight: 0) and also for bodyweight-only strength
              exercises (unit 'bw'/'assist' are skipped by that legacy calculation). Nesting the chart
              inside that same condition, as it was before this change, meant the chart could never
              render for either case regardless of what getExerciseProgress returned. The chart has its
              own independent data source (progressData, fetched above) and its own empty state
              (noProgress message below), so it only needs to know whether the viewer is authenticated -
              matching the fetch effect's own !isPublicMode guard. */}
          {!isPublicMode && (
            <div className={styles.progressChart}>
              <h4 className={styles.progressTitle}>
                {isHoldTimeType ? t('exercises.modal.holdTimeProgress') : t('exercises.modal.weightProgress')}
              </h4>
              <div className={styles.chartContainer}>
                {progressData.length > 0 ? (
                  <svg viewBox="0 0 400 200" className={styles.progressSvg}>
                    {/* Grid lines */}
                    {[0, 1, 2, 3, 4].map(i => (
                      <line
                        key={`h-${i}`}
                        x1="40"
                        y1={20 + i * 40}
                        x2="380"
                        y2={20 + i * 40}
                        stroke="#444"
                        strokeWidth="1"
                      />
                    ))}
                    {progressData.map((_, i) => (
                      <line
                        key={`v-${i}`}
                        x1={40 + i * (340 / Math.max(progressData.length - 1, 1))}
                        y1="20"
                        x2={40 + i * (340 / Math.max(progressData.length - 1, 1))}
                        y2="180"
                        stroke="#444"
                        strokeWidth="1"
                      />
                    ))}

                    {/* Progress line */}
                    {(() => {
                      const validData = progressData.filter(d => d.value >= 0);
                      if (validData.length > 1) {
                        const maxValue = Math.max(...validData.map(d => d.value));
                        return (
                          <polyline
                            points={progressData.map((data, i) => {
                              const x = 40 + i * (340 / Math.max(progressData.length - 1, 1));
                              const y = data.value >= 0
                                ? 180 - ((data.value / maxValue) * 160)
                                : 180; // Blank points at bottom
                              return `${x},${y}`;
                            }).join(' ')}
                            fill="none"
                            stroke="#4CAF50"
                            strokeWidth="2"
                          />
                        );
                      }
                      return null;
                    })()}

                    {/* Data points - only show actual data, not blanks */}
                    {progressData.map((data, i) => {
                      if (data.value < 0) return null; // Skip autofill blanks

                      const validData = progressData.filter(d => d.value >= 0);
                      if (validData.length === 0) return null;

                      const maxValue = Math.max(...validData.map(d => d.value));
                      const x = 40 + i * (340 / Math.max(progressData.length - 1, 1));
                      const y = 180 - ((data.value / maxValue) * 160);
                      return (
                        <g key={i}>
                          <circle
                            cx={x}
                            cy={y}
                            r="6"
                            fill="#4CAF50"
                            stroke="#fff"
                            strokeWidth="2"
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={() => setHoveredPoint({ ...data, x, y })}
                            onMouseLeave={() => setHoveredPoint(null)}
                          />
                          {hoveredPoint && hoveredPoint.date === data.date && (
                            <foreignObject x={x + 8} y={y + 8} width="120" height="35">
                              <div className={styles.tooltip}>
                                <div className={styles.tooltipDate}>
                                  {new Date(data.date).toLocaleDateString()}
                                </div>
                                <div className={styles.tooltipWeight}>
                                  {isHoldTimeType
                                    ? t('progress.modal.personalRecords.secondsLabel', { count: data.value })
                                    : `${data.value} ${data.unit}`}
                                </div>
                              </div>
                            </foreignObject>
                          )}
                        </g>
                      );
                    })}

                    {/* Y-axis labels (weight or hold time, depending on exerciseType) */}
                    {(() => {
                      const validData = progressData.filter(d => d.value >= 0);
                      if (validData.length === 0) return null;
                      const maxValue = Math.max(...validData.map(d => d.value));
                      return [0, 0.25, 0.5, 0.75, 1].map(ratio => (
                        <text
                          key={`y-${ratio}`}
                          x="30"
                          y={180 - (ratio * 160)}
                          fill="#999"
                          fontSize="12"
                          textAnchor="end"
                        >
                          {Math.round(maxValue * ratio)}
                        </text>
                      ));
                    })()}

                    {/* X-axis labels (dates) */}
                    {progressData.map((data, i) => {
                      const x = 40 + i * (340 / Math.max(progressData.length - 1, 1));
                      const date = new Date(data.date);
                      const label = date.toLocaleDateString('en-US', { month: 'short' });
                      return (
                        <text
                          key={i}
                          x={x}
                          y="195"
                          fill="#999"
                          fontSize="10"
                          textAnchor="middle"
                        >
                          {label}
                        </text>
                      );
                    })}
                  </svg>
                ) : (
                  <div className={styles.noDataMessage}>
                    {t('exercises.modal.noProgress')}
                  </div>
                )}
              </div>
            </div>
          )}
          {exercise.references && exercise.references.length > 0 && (
            <div className={styles.referencesSection}>
              <button 
                className={styles.referencesToggle}
                onClick={() => setReferencesExpanded(!referencesExpanded)}
              >
                <h4 className={styles.referencesHeading}>{t('exercises.modal.videoReferences')}</h4>
                <span className={`${styles.toggleArrow} ${referencesExpanded ? styles.expanded : ''}`}>
                  ▼
                </span>
              </button>
              {referencesExpanded && (
                <>
                  <div className={styles.referencesList}>
                    {exercise.references.map((url, index) => (
                      <InstagramEmbed key={index} url={url} />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className={styles.detailActions}>
            <div className={styles.actionsRow}>
              <button
                onClick={() => onAddToWorkout(exercise)}
                className={`${styles.btn} ${styles.btnSecondary}`}
              >
                {t('exercises.modal.addToWorkout')}
              </button>
              <button
                onClick={() => onToggleFavorite(exercise)}
                className={`${styles.btn} ${styles.btnSecondary}`}
                disabled={!canUseProtectedActions}
                style={!canUseProtectedActions ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              >
                {isFavorite ? t('exercises.modal.removeFavorite') : t('exercises.modal.addFavorite')}
              </button>
              <button onClick={internalOnClose} className={`${styles.btn} ${styles.btnSecondary}`}>
                {t('exercises.modal.close')}
              </button>
            </div>

            {canEdit && (
              <div className={styles.actionsRow}>
                <button
                  onClick={() => onEditExercise(exercise)}
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  disabled={!canUseProtectedActions}
                  style={!canUseProtectedActions ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                >
                  {t('exercises.modal.edit')}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className={`${styles.btn} ${styles.btnDanger}`}
                  disabled={!canUseProtectedActions || isDeleting}
                  style={!canUseProtectedActions ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                >
                  {isDeleting ? t('exercises.modal.deleting') : t('exercises.modal.delete')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title={t('exercises.modal.deleteTitle')}
        message={t('exercises.modal.deleteMessage')}
        confirmText={isDeleting ? t('exercises.modal.deleting') : t('exercises.modal.delete')}
        cancelText={t('exercises.modal.cancel')}
        isDangerous={true}
        onCancel={() => {
          if (isDeleting) return;
          setShowDeleteConfirm(false);
        }}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ExerciseDetailModal;
